import { useMemo } from 'react';
import { BodyParagraphData, BodyParagraphType } from '@/types/writing';

// Helper functions (can be moved to utils if preferred)
function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function joinEssaySections(
  intro: string,
  body: { type: BodyParagraphType | ''; content: string }[],
  concl: string
): string {
  return [intro, ...body.map((p) => p.content), concl]
    .filter(Boolean)
    .join('\n\n');
}

interface UseEssayDerivedStateProps {
  introduction: string;
  bodyParagraphs: BodyParagraphData[];
  conclusion: string;
}

export function useEssayDerivedState({
  introduction,
  bodyParagraphs,
  conclusion,
}: UseEssayDerivedStateProps) {
  const derivedState = useMemo(() => {
    const introWordCount = countWords(introduction);
    const bodyWordCount = bodyParagraphs.reduce(
      (sum, p) => sum + countWords(p.content),
      0
    );
    const conclusionWordCount = countWords(conclusion);
    const totalWordCount = introWordCount + bodyWordCount + conclusionWordCount;

    const currentContent = joinEssaySections(
      introduction,
      bodyParagraphs,
      conclusion
    );

    const hasIntro = introduction.trim().length > 0;
    const hasConclusion = conclusion.trim().length > 0;
    const hasMinTwoBodyParas = bodyParagraphs.length >= 2;
    const allBodyParasNonEmpty = bodyParagraphs.every(
      (p) => p.content.trim().length > 0
    );
    const meetsWordCount = totalWordCount >= 250;
    const allSectionsFilled =
      hasIntro && hasConclusion && hasMinTwoBodyParas && allBodyParasNonEmpty;
    const canEvaluate = meetsWordCount && allSectionsFilled;

    const requirementsList: string[] = [];
    if (!meetsWordCount)
      requirementsList.push(`Minimum 250 words (currently ${totalWordCount})`);
    if (!hasIntro) requirementsList.push('Introduction content required');
    if (!hasMinTwoBodyParas)
      requirementsList.push('At least 2 body paragraphs required');
    if (!allBodyParasNonEmpty)
      requirementsList.push('Content in all body paragraphs required');
    if (!hasConclusion) requirementsList.push('Conclusion content required');

    const requirementText = canEvaluate
      ? 'Ready for evaluation'
      : 'Requirements not met';

    return {
      totalWordCount,
      currentContent,
      canEvaluate,
      meetsWordCount,
      allSectionsFilled,
      requirementsList,
      requirementText,
    };
  }, [introduction, bodyParagraphs, conclusion]);

  return derivedState;
}
